"use client";

import React from "react";
import { Box, ShieldCheck, RefreshCw, Pill, Truck, CreditCard, MessageCircleQuestionMark } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqCategories = [{
  id: "connectivity",
  label: "eSIM & Connectivity",
  icon: RefreshCw,
  questions: [{
    q: "How do I activate my WorldAccess eSIM?",
    a: "Activation is instant. After completing your crypto payment, you will receive a QR code via email. Simply scan it with your compatible device's camera to install the cellular plan. No physical SIM card or shipping required."
  }, {
    q: "Which countries are covered by your data plans?",
    a: "We offer global coverage in over 190 countries. You can choose from regional plans (like Europe or Asia) or global plans depending on your travel needs. Detailed coverage maps are available on each product page."
  }, {
    q: "Can I use my eSIM for hotspots and tethering?",
    a: "Yes, most of our eSIM plans support personal hotspots and tethering, allowing you to share your high-speed data connection with your laptop or other devices while traveling."
  }, {
    q: "Does my data plan expire?",
    a: "Data plans typically have a validity period ranging from 7 to 30 days starting from the moment of activation. We recommend activating your plan only when you arrive at your destination."
  }]
}, {
  id: "vps",
  label: "VPS & Hosting",
  icon: Box,
  questions: [{
    q: "Where are your VPS servers located?",
    a: "We operate high-performance nodes in 12 global locations including Frankfurt, Singapore, New York, and Tokyo. This ensures low latency and high availability for your applications regardless of your location."
  }, {
    q: "Do I get full root access to my server?",
    a: "Absolutely. Every WorldAccess VPS comes with full root access (Linux) or Administrator access (Windows). You have complete control over your environment, software installations, and security configurations."
  }, {
    q: "What is your uptime guarantee?",
    a: "We provide a 99.9% uptime SLA for all our hosting services. Our infrastructure is built on enterprise-grade hardware with redundant power and network uplinks to ensure your services stay online."
  }]
}, {
  id: "crypto",
  label: "Crypto Payments",
  icon: CreditCard,
  questions: [{
    q: "Which cryptocurrencies do you accept?",
    a: "We accept a wide range of digital assets including Bitcoin (BTC), Ethereum (ETH), Solana (SOL), and stablecoins like USDT and USDC. We also support the Lightning Network for near-instant Bitcoin transactions."
  }, {
    q: "How many network confirmations are required?",
    a: "For Bitcoin, we typically require 2 confirmations. For Ethereum and Solana, services are usually provisioned within minutes of the transaction appearing on the blockchain. Lightning payments are instant."
  }, {
    q: "Are there any hidden transaction fees?",
    a: "WorldAccess does not charge additional payment processing fees. You only pay the network fee (gas) required by the blockchain to process your transaction. We recommend using low-fee networks for smaller purchases."
  }]
}, {
  id: "privacy",
  label: "Privacy & Security",
  icon: ShieldCheck,
  questions: [{
    q: "Do you keep logs of my activity?",
    a: "No. Privacy is at our core. We operate under a strict no-logs policy for our VPN and VPS services. We do not track, collect, or share your browsing data or server activity with any third parties."
  }, {
    q: "Can I sign up without providing personal info?",
    a: "Yes. Our platform is designed for privacy-conscious users. You can sign up with just an email address and pay with crypto. We do not require KYC or physical addresses for our digital services."
  }]
}, {
  id: "account",
  label: "Support & Billing",
  icon: MessageCircleQuestionMark,
  questions: [{
    q: "How do I renew my digital services?",
    a: "You can manually renew your services through the dashboard using your preferred cryptocurrency. We will send you an email notification 3 days before your service is set to expire."
  }, {
    q: "What is your refund policy for digital goods?",
    a: "Due to the nature of digital keys and data plans, we offer refunds only if the service is technically non-functional. Refund requests must be submitted within 24 hours of purchase via our support ticket system."
  }]
}];
export default function KnowledgeBase() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="32188" id="knowledge-base" className="bg-background py-16 md:py-24 relative overflow-hidden dark" ref={ref}>
      <div className={`container mx-auto px-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        
        {}
        <div className="mb-12 md:mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4 tracking-tighter">
            Knowledge Base
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Technical documentation and answers for WorldAccess eSIM, VPS, and crypto-enabled services.
          </p>
        </div>

        {}
        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue="connectivity" orientation="vertical" className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            
            {}
            <div className="w-full lg:w-64 shrink-0">
              <div className="sticky top-24 space-y-8">
                <div>
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-4 px-2 hidden lg:block">
                    Service Categories
                  </h3>
                  <TabsList className="flex flex-row lg:flex-col h-auto w-full bg-transparent p-0 gap-2 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 no-scrollbar items-start justify-start">
                    {faqCategories.map((category, i) => {
                    const Icon = category.icon;
                    return <TabsTrigger key={category.id} value={category.id} data-index={i} className="w-auto lg:w-full justify-start gap-3 px-4 py-3 h-auto text-sm font-medium rounded-sm border border-transparent 
                            data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:border-primary/20 
                            hover:bg-secondary transition-all whitespace-nowrap shrink-0">
                          <Icon className="size-4 shrink-0" />
                          {category.label}
                        </TabsTrigger>;
                  })}
                  </TabsList>
                </div>

                {}
                <div className="hidden lg:block p-6 bg-card/50 backdrop-blur-md rounded-sm border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-primary/10 rounded-full text-primary">
                      <MessageCircleQuestionMark className="size-5" />
                    </div>
                    <h4 className="font-default font-bold text-foreground">Need Help?</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed font-default">
                    Can't find the technical specification you need? Our team is available 24/7.
                  </p>
                  <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm rounded-sm">
                    <Link to="#">Open Support Ticket</Link>
                  </Button>
                </div>
              </div>
            </div>

            {}
            <div className="flex-1 min-w-0">
              {faqCategories.map((category, i) => <TabsContent key={category.id} value={category.id} className="mt-0 focus-visible:outline-none animate-in fade-in slide-in-from-right-4 duration-300" data-index={i}>
                  <div className="bg-card/30 backdrop-blur-sm rounded-sm border border-border shadow-md overflow-hidden">
                    <div className="p-6 md:p-8 border-b border-border bg-muted/20 flex items-center gap-4">
                      <div className="p-3 bg-background rounded-sm border border-border shadow-xs text-primary">
                        <category.icon className="size-6" />
                      </div>
                      <div>
                        <h3 className="text-xl md:text-2xl font-default font-bold text-foreground tracking-tight">
                          {category.label}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1 font-default">
                          Essential information regarding {category.label.toLowerCase()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-6 md:p-8">
                      <Accordion type="single" collapsible className="w-full">
                        {category.questions.map((item, index) => <AccordionItem key={index} value={`item-${index}`} className="border-b border-border last:border-0" data-index={index}>
                            <AccordionTrigger className="text-left font-bold text-foreground hover:text-primary hover:no-underline py-5 text-base md:text-lg font-default transition-colors">
                              {item.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6 font-default">
                              {item.a}
                            </AccordionContent>
                          </AccordionItem>)}
                      </Accordion>
                    </div>
                  </div>
                </TabsContent>)}

              {}
              <div className="mt-8 p-6 bg-card/50 backdrop-blur-md rounded-sm border border-border lg:hidden text-center">
                <h4 className="font-default font-bold text-foreground mb-2">Still have questions?</h4>
                <p className="text-sm text-muted-foreground mb-4 font-default">
                  Can't find the answer you're looking for? Our support team is here to help.
                </p>
                <Button asChild className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm">
                  <Link to="#">Contact Support</Link>
                </Button>
              </div>
            </div>

          </Tabs>
        </div>
      </div>
    </section>;
}